import { useEffect } from 'react';
import { Providers } from './app/Providers.jsx';
import { AppRouter } from './app/router.jsx';
import { AppErrorBoundary } from './app/ErrorBoundary.jsx';

export default function App() {
  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let checking = false;

    async function checkVersion() {
      if (disposed || checking) return;
      checking = true;

      try {
        const response = await fetch(`/meta.json?t=${Date.now()}`, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Version check failed: ${response.status}`);

        const { version } = await response.json();
        if (disposed) return;
        if (!Number.isSafeInteger(version) || version <= 0) {
          throw new Error('Invalid app version in meta.json');
        }

        const currentVersion = String(version);
        const savedVersion = localStorage.getItem('app_version');
        if (savedVersion === currentVersion) return;

        localStorage.setItem('app_version', currentVersion);
        if (savedVersion === null) return;

        // Cache Storage is unavailable in some browsers and native WebViews.
        // A cache error must not prevent reloading after saving the new version.
        try {
          if (window.caches) {
            const keys = await window.caches.keys();
            if (disposed) return;
            await Promise.all(keys.map((key) => window.caches.delete(key)));
          }
        } catch (error) {
          if (!disposed) console.warn('Could not clear app caches:', error);
        }

        if (!disposed) window.location.reload();
      } catch (error) {
        if (!disposed) console.warn('Could not check app version:', error);
      } finally {
        checking = false;
      }
    }

    void checkVersion();
    const interval = window.setInterval(checkVersion, 10 * 60 * 1000);

    return () => {
      disposed = true;
      controller.abort();
      window.clearInterval(interval);
    };
  }, []);

  return (
    <Providers>
      <AppErrorBoundary>
        <AppRouter />
      </AppErrorBoundary>
    </Providers>
  );
}
